import OT from "@opentok/client";

import type { VideoDimensions, deviceKind, Device } from "./definitions";
import { type ComposerTranslation } from "vue-i18n";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { danger, log } from "@/Utils/Debug";

import {
  PARTICIPANT_MAX_HEIGHT,
  PARTICIPANT_MAX_WIDTH,
  PARTICIPANT_MAX_HEIGHT_MOBILE,
  PARTICIPANT_MAX_WIDTH_MOBILE,
} from "./constants";
import { isApp } from "@/Utils/Ionic";

export const getInitials = (name: string | undefined): string => {
  if (name) {
    const response: string[] = [];
    const splitedName = name.split(" ");

    splitedName.forEach((item) => response.push(item[0]));

    return response.length > 2 ? `${response[0]}${response[1]}` : response.join("");
  } else {
    return "";
  }
};

export const generateParticipantStyles = (
  dimensions: VideoDimensions,
  isMobile: boolean
): Record<string, string> => {
  const ensureInt = (val: number | undefined, defaultVal: number) => {
    if (hasValue(val) && Number.isInteger(val) && val > 0) {
      return val;
    }

    return defaultVal;
  };

  const maxHeight = isMobile ? PARTICIPANT_MAX_HEIGHT_MOBILE : PARTICIPANT_MAX_HEIGHT;
  const maxWidth = isMobile ? PARTICIPANT_MAX_WIDTH_MOBILE : PARTICIPANT_MAX_WIDTH;
  const styles: Record<string, string> = {
    "aspect-ratio": `${ensureInt(dimensions.width, isMobile ? PARTICIPANT_MAX_WIDTH_MOBILE : PARTICIPANT_MAX_WIDTH)} / ${ensureInt(dimensions.height, isMobile ? PARTICIPANT_MAX_HEIGHT_MOBILE : PARTICIPANT_MAX_HEIGHT)}`,
  };

  if (dimensions.height !== 0 && dimensions.width !== 0 && dimensions.height > dimensions.width) {
    styles.height = `${maxHeight}px`;
  } else {
    styles.width = `${maxWidth}px`;
  }

  return styles;
};

export const isBackCamera = (deviceName: string | undefined): boolean => {
  if (!isValidString(deviceName)) return false;

  const wordsToSearc = [
    "back",
    "rear",
    "environment",
    "trasera",
    "entorno",
    "posterior",
    "dietro",
    "indietro",
    "retro",
    "posteriore",
    "ambiente",
    "contesto",
  ];

  let cent = false;
  let i = 0;

  while (!cent && i < wordsToSearc.length) {
    if (deviceName.toLowerCase().includes(wordsToSearc[i])) {
      cent = true;
    }

    i++;
  }

  return cent;
};

const containsSearchText = (label: string, searchText: string): boolean => {
  return new RegExp(searchText, "gi").test(label);
};

const getVideoDeviceLabel = (label: string, t: ComposerTranslation): string => {
  if (containsSearchText(label, "front") || containsSearchText(label, "user")) {
    return t("viceocall.devices.camera.front");
  } else if (containsSearchText(label, "back") || containsSearchText(label, "environment")) {
    return t("viceocall.devices.camera.back");
  } else if (containsSearchText(label, "default") || !isValidString(label)) {
    return t("viceocall.devices.camera.default");
  } else if (isApp()) {
    return `${t("viceocall.devices.camera.other")} - ${label}`;
  }

  return label;
};

const getAudioDeviceLabel = (label: string, t: ComposerTranslation): string => {
  if (containsSearchText(label, "default") || !isValidString(label)) {
    return t("viceocall.devices.microphone.default");
  } else if (containsSearchText(label, "speaker")) {
    return `${t("viceocall.devices.microphone.speaker")} - ${label}`;
  } else if (containsSearchText(label, "headset")) {
    return `${t("viceocall.devices.microphone.headset")} - ${label}`;
  } else if (isApp()) {
    return `${t("viceocall.devices.microphone.other")} - ${label}`;
  }

  return label;
};

const getDeviceLabel = (device: Device, t: ComposerTranslation): string => {
  switch (device.kind) {
    case "videoInput":
      return getVideoDeviceLabel(device.label, t);
    case "audioInput":
    case "audioOutput":
      return getAudioDeviceLabel(device.label, t);
    default:
      return device.label;
  }
};

export const getDevices = async (t: ComposerTranslation): Promise<Device[]> => {
  const isMicrosoft = (string: string | null | undefined): boolean => {
    return hasValue(string) && string.toLowerCase().includes("microsoft");
  };

  return new Promise<Device[]>((resolve) => {
    let devices: Device[] = [];

    OT.getDevices((error, devs) => {
      log("Devices", JSON.stringify(devs));

      if (error || !hasValue(devs)) {
        danger("Error getDevices", error);

        devices = [];
      } else {
        devices = devs.filter((device) => !isMicrosoft(device.label));
      }

      OT.getAudioOutputDevices().then((outputDevices) => {
        log("Output devices", JSON.stringify(outputDevices));

        const validDevices = outputDevices.filter(
          (device) => hasValue(device.deviceId) && !isMicrosoft(device.label)
        ) as Device[];

        if (validDevices.length > 0) {
          devices = devices.concat(validDevices.map((device) => ({ ...device, kind: "audioOutput" })));
        }

        resolve(devices);
      });
    });
  }).then((devices: Device[]) =>
    devices.map((device) => ({
      ...device,
      label: getDeviceLabel(device, t),
      kind: device.kind as deviceKind,
    }))
  );
};
